import { useEffect, useState } from "react";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendingUp, MailOpen, AlertTriangle, Send } from "lucide-react";

const COLORS = ["#1a2b48", "#007bff", "#10b981", "#f59e0b"];

export default function StatsPage() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/stats/dashboard").then((r) => setStats(r.data));
    }, []);

    if (!stats) return <div className="text-slate-400 text-sm">Lädt ...</div>;

    const countryData = Object.entries(stats.contacts.by_country).map(([k, v]) => ({ name: k, value: v }));
    const langData = Object.entries(stats.contacts.by_language).map(([k, v]) => ({ name: k, value: v }));
    const funnelData = [
        { name: "Gesendet", value: stats.emails.sent },
        { name: "Geöffnet", value: stats.emails.opened },
        { name: "Geklickt", value: stats.emails.clicked },
        { name: "Bounced", value: stats.emails.bounced },
    ];

    return (
        <div className="space-y-8 animate-fade-in-up" data-testid="stats-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-[#007bff] font-bold mb-2">Analyse</div>
                <h1 className="font-display text-5xl text-[#1a2b48] leading-none">Statistik.</h1>
                <p className="text-slate-500 mt-3 max-w-xl">Performance Ihrer Outreach-Kampagnen im Überblick.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Card className="p-6 bg-white border-slate-200 rounded-xl">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Send className="w-5 h-5 text-[#007bff]" /></div>
                        <div>
                            <h3 className="font-semibold text-[#1a2b48]">Versand-Funnel</h3>
                            <p className="text-xs text-slate-500">Gesendet → Geöffnet → Geklickt</p>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={funnelData}>
                            <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                            <YAxis stroke="#94a3b8" fontSize={12} />
                            <Tooltip />
                            <Bar dataKey="value" fill="#007bff" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                <Card className="p-6 bg-white border-slate-200 rounded-xl">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-[#007bff]" /></div>
                        <div>
                            <h3 className="font-semibold text-[#1a2b48]">Kontakte nach Land</h3>
                            <p className="text-xs text-slate-500">DE / AT / CH</p>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                            <Pie data={countryData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                                {countryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Legend />
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </Card>

                <Card className="p-6 bg-white border-slate-200 rounded-xl">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><MailOpen className="w-5 h-5 text-emerald-600" /></div>
                        <div>
                            <h3 className="font-semibold text-[#1a2b48]">Sprach-Verteilung</h3>
                            <p className="text-xs text-slate-500">DE / EN / FR / IT</p>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={langData}>
                            <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                            <YAxis stroke="#94a3b8" fontSize={12} />
                            <Tooltip />
                            <Bar dataKey="value" fill="#1a2b48" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                <Card className="p-6 bg-white border-slate-200 rounded-xl">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-rose-600" /></div>
                        <div>
                            <h3 className="font-semibold text-[#1a2b48]">Engagement-Raten</h3>
                            <p className="text-xs text-slate-500">Performance-Indikatoren</p>
                        </div>
                    </div>
                    <div className="space-y-5 mt-6">
                        <Rate label="Öffnungsrate" value={stats.emails.open_rate} color="#10b981" />
                        <Rate label="Bounce Rate" value={stats.emails.bounce_rate} color="#ef4444" />
                    </div>
                </Card>
            </div>
        </div>
    );
}

function Rate({ label, value, color }) {
    return (
        <div>
            <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-600 font-medium">{label}</span>
                <span className="font-display text-2xl text-[#1a2b48]">{value}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(value, 100)}%`, background: color }} />
            </div>
        </div>
    );
}
